import { DoughnutController, ArcElement, } from "chart.js";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, BarElement, TimeSeriesScale, TimeScale, LogarithmicScale, _adapters, } from "chart.js";
import { parse, parseISO, toDate, isValid, format, startOfSecond, startOfMinute, startOfHour, startOfDay, startOfWeek, startOfMonth, startOfQuarter, startOfYear, addMilliseconds, addSeconds, addMinutes, addHours, addDays, addWeeks, addMonths, addQuarters, addYears, differenceInMilliseconds, differenceInSeconds, differenceInMinutes, differenceInHours, differenceInDays, differenceInWeeks, differenceInMonths, differenceInQuarters, differenceInYears, endOfSecond, endOfMinute, endOfHour, endOfDay, endOfWeek, endOfMonth, endOfQuarter, endOfYear, } from "date-fns";
const FORMATS = {
    datetime: "MMM d, yyyy, h:mm:ss aaaa",
    millisecond: "h:mm:ss.SSS aaaa",
    second: "h:mm:ss aaaa",
    minute: "h:mm aaaa",
    hour: "HH",
    day: "MMM d",
    week: "PP",
    month: "MMM yyyy",
    quarter: "qqq - yyyy",
    year: "yyyy",
};
_adapters._date.override({
    formats: function () {
        return FORMATS;
    },
    parse: function (value, fmt) {
        if (value === null || typeof value === "undefined") {
            return null;
        }
        const type = typeof value;
        if (type === "number" || value instanceof Date) {
            value = toDate(value);
        }
        else if (type === "string") {
            if (typeof fmt === "string") {
                value = parse(value, fmt, new Date(), this.options);
            }
            else {
                value = parseISO(value, this.options);
            }
        }
        return isValid(value) ? value.getTime() : null;
    },
    format: function (time, fmt) {
        return format(time, fmt, this.options);
    },
    add: function (time, amount, unit) {
        switch (unit) {
            case "millisecond":
                return addMilliseconds(time, amount);
            case "second":
                return addSeconds(time, amount);
            case "minute":
                return addMinutes(time, amount);
            case "hour":
                return addHours(time, amount);
            case "day":
                return addDays(time, amount);
            case "week":
                return addWeeks(time, amount);
            case "month":
                return addMonths(time, amount);
            case "quarter":
                return addQuarters(time, amount);
            case "year":
                return addYears(time, amount);
            default:
                return time;
        }
    },
    diff: function (max, min, unit) {
        switch (unit) {
            case "millisecond":
                return differenceInMilliseconds(max, min);
            case "second":
                return differenceInSeconds(max, min);
            case "minute":
                return differenceInMinutes(max, min);
            case "hour":
                return differenceInHours(max, min);
            case "day":
                return differenceInDays(max, min);
            case "week":
                return differenceInWeeks(max, min);
            case "month":
                return differenceInMonths(max, min);
            case "quarter":
                return differenceInQuarters(max, min);
            case "year":
                return differenceInYears(max, min);
            default:
                return 0;
        }
    },
    startOf: function (time, unit, weekday) {
        switch (unit) {
            case "second":
                return startOfSecond(time);
            case "minute":
                return startOfMinute(time);
            case "hour":
                return startOfHour(time);
            case "day":
                return startOfDay(time);
            case "week":
                return startOfWeek(time);
            case "isoWeek":
                return startOfWeek(time, { weekStartsOn: +weekday });
            case "month":
                return startOfMonth(time);
            case "quarter":
                return startOfQuarter(time);
            case "year":
                return startOfYear(time);
            default:
                return time;
        }
    },
    endOf: function (time, unit) {
        switch (unit) {
            case "second":
                return endOfSecond(time);
            case "minute":
                return endOfMinute(time);
            case "hour":
                return endOfHour(time);
            case "day":
                return endOfDay(time);
            case "week":
                return endOfWeek(time);
            case "month":
                return endOfMonth(time);
            case "quarter":
                return endOfQuarter(time);
            case "year":
                return endOfYear(time);
            default:
                return time;
        }
    },
});
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Title, Tooltip, Legend, Filler, BarElement, TimeSeriesScale, TimeScale, LogarithmicScale, DoughnutController);
export const getChartOptions = (title, extra) => {
    var _a, _b, _c;
    return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: "index" },
        plugins: {
            title: {
                display: true,
                text: title,
                font: {
                    family: "Source Sans Pro",
                    size: 14,
                },
            },
            legend: {
                display: (_a = extra === null || extra === void 0 ? void 0 : extra.displayLegend) !== null && _a !== void 0 ? _a : false,
                position: (_b = extra === null || extra === void 0 ? void 0 : extra.legendPos) !== null && _b !== void 0 ? _b : "top",
                font: {
                    family: "Source Sans Pro",
                },
            },
            tooltip: {
                backgroundColor: "hsl(238, 100%, 15%)",
                padding: 10,
                itemSort: (a, b) => a.element.y - b.element.y,
                font: {
                    family: "Source Sans Pro",
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0,
                    count: (_c = extra === null || extra === void 0 ? void 0 : extra.yTicksCount) !== null && _c !== void 0 ? _c : undefined,
                    maxTicksLimit: 8,
                },
                grace: "5%",
            },
        },
        elements: {
            line: {
                borderColor: "#4b4bf3",
                backgroundColor: "#ababab3d",
                fill: true,
                tension: 0.2,
            },
            point: {
                backgroundColor: "#4b4bf3",
                borderColor: "#4b4bf3",
                radius: 1,
                hoverRadius: 5,
            },
            bar: {
                borderRadius: 6,
                borderColor: "#5b5bf1",
                backgroundColor: "#5b5bf1",
            },
        },
    };
};
export function withSecondaryColor() {
    return {
        backgroundColor: "hsla(49, 100%, 67%, .15)",
        borderColor: "hsl(49, 100%, 64%)",
        pointBackgroundColor: "hsl(49, 100%, 64%)",
    };
}
export function getChartColor(i, type) {
    const colors = [
        {
            backgroundColor: "#ababab3d",
            borderColor: "#4b4bf3",
            pointBackgroundColor: "#4b4bf3",
        },
        {
            backgroundColor: "hsla(49, 100%, 67%, .1)",
            borderColor: "hsl(49, 100%, 64%)",
            pointBackgroundColor: "hsl(49, 100%, 64%)",
        },
        {
            backgroundColor: "hsla(346, 89%, 63%, 0.1)",
            borderColor: "#F54E74",
            pointBackgroundColor: "#F54E74",
        },
        {
            backgroundColor: "hsla(128, 88%, 66%, 0.1)",
            borderColor: "#5DF572",
            pointBackgroundColor: "#5DF572",
        },
        {
            backgroundColor: "hsla(27, 92%, 53%, 0.1)",
            borderColor: "#F57D1B",
            pointBackgroundColor: "#F57D1B",
        },
        {
            backgroundColor: "hsl(189, 77%, 56%, 0.1)",
            borderColor: "hsl(189, 77%, 56%)",
            pointBackgroundColor: "hsl(189, 77%, 56%)",
        },
        {
            backgroundColor: "hsl(333, 29%, 43%, 0.1)",
            borderColor: "hsl(333, 29%, 43%)",
            pointBackgroundColor: "hsl(333, 29%, 43%)",
        },
    ];
    const color = structuredClone(colors[i % colors.length]);
    if (type === "bar") {
        color.backgroundColor = color.borderColor;
    }
    return color;
}
export const timeFormats = {
    millisecond: "d MMM hh:mm",
    second: "d MMM hh:mm",
    minute: "d MMM hh:mm",
    hour: "d MMM hh:00",
    day: "d MMM",
    week: "d MMM",
    month: "LLLL yyyy",
    quarter: "LLLL yyyy",
    year: "yyyy",
};
export function getUnitTimeFormat(unit) {
    return timeFormats[unit];
}
